import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import api, { errMsg } from '../../../lib/api';
import { Star } from 'lucide-react';

// Find cover for one session on one date: ranked available staff + cancel / restore.
export const CoverDialog = ({ block, onClose, onDone, onEdit }) => {
  const [cands, setCands] = useState(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!block) return;
    setCands(null); setNote('');
    api.get('/ludo/schedule/suggest', { params: { session_id: block.session_id, date_: block.date } })
      .then((r) => setCands(r.data.candidates)).catch((e) => { toast.error(errMsg(e)); setCands([]); });
  }, [block]);
  const act = async (action, staff_id) => {
    try {
      await api.post('/ludo/schedule/overrides', { session_id: block.session_id, date: block.date, action, staff_id, note });
      toast.success(action === 'reassign' ? 'Cover assigned for this date' : action === 'cancel' ? 'Cancelled for this date' : 'Back to the regular schedule');
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const end = async () => {
    if (!window.confirm('End this repeating session for all future weeks?')) return;
    try { await api.delete(`/ludo/schedule/sessions/${block.session_id}`); toast.success('Repeating session ended'); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!block} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sched-cover-dialog">
        {block && (<>
          <DialogHeader><DialogTitle className="font-serif">{block.client_name} · {block.date} · {block.start}–{block.end}</DialogTitle></DialogHeader>
          {block.issues?.length > 0 && <div className="text-[13px] text-[#8A3A3A]" data-testid="sched-cover-issues">{block.issues.join(' · ')}</div>}
          <div className="text-[11px] uppercase tracking-wider text-[#6E5F78] font-semibold">Available to cover (best first)</div>
          {cands === null && <div className="text-sm text-[#685B72]">Checking availability…</div>}
          {cands?.length === 0 && <div className="text-sm text-[#685B72]" data-testid="sched-cover-none">Nobody is free for this whole block. Try splitting it or cancelling for today.</div>}
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {(cands || []).map((c) => (
              <div key={c.staff_id} className="flex items-center justify-between rounded-xl border border-[#E5E0EA] px-3 py-2" data-testid={`sched-cand-${c.staff_id}`}>
                <div className="text-sm">
                  <b>{c.name}</b> <span className="text-[11px] text-[#8A7B95]">{c.role} · {c.week_hours} hrs this week</span>
                  {c.knows_client && <span className="ml-2 text-[11px] text-[#3F7D6E] inline-flex items-center gap-0.5"><Star size={10} /> knows client</span>}
                  {c.would_exceed_max && <span className="ml-2 text-[11px] text-[#8A3A3A]">over max hrs</span>}
                </div>
                <Button size="sm" onClick={() => act('reassign', c.staff_id)} className="rounded-full bg-[#5E5068] text-white h-7" data-testid={`sched-assign-${c.staff_id}`}>Assign</Button>
              </div>
            ))}
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for this date (optional)" className="rounded-xl h-9" data-testid="sched-cover-note" />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => act('cancel')} className="rounded-full" data-testid="sched-cancel-date">Cancel this date</Button>
            {(block.reassigned || block.status === 'cancelled') && <Button variant="outline" onClick={() => act('restore')} className="rounded-full" data-testid="sched-restore-date">Restore regular</Button>}
            <Button variant="outline" onClick={() => onEdit(block)} className="rounded-full" data-testid="sched-edit-recurring">Edit repeating session</Button>
            <Button variant="ghost" onClick={end} className="rounded-full text-[#8A3A3A]" data-testid="sched-end-recurring">End repeating session</Button>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};

export default CoverDialog;
